import {Signal} from "../../../models"

// Utils
import Utils from "../../../../../utils/Utils"

import {countLeadsWithData, DEFAULT_SAMPLE_RATE, PAGE_FRACTION, pageSamplesFor, windowSamplesFor} from "./geometry"


export type EcgWindowInfo = {
    totalSec: number
    pageSec: number
    rowSec: number
    maxStartSec: number
    paged: boolean
    strip: boolean
}

/**
 * @param signals
 */
export function legacyWindowSignals(signals: Signal[]): Signal[] {
    return signals.map(signal => {
        if (!signal || !signal.data) {
            return signal
        }

        const capped = Utils.normalizeLeadDataSize(signal.sampleRate, signal.data);

        return {...signal, data: capped.slice(0, Math.floor(capped.length * PAGE_FRACTION))}
    })
}

/**
 * @param ecgData
 * @param longRecords the per-account feature — see ../longRecordPilot
 */
export function ecgWindowInfo(ecgData: Signal[], longRecords: boolean = false): EcgWindowInfo {
    const reference = ecgData[0];
    const sampleRate = (reference && reference.sampleRate) || DEFAULT_SAMPLE_RATE;
    const totalSamples = reference && reference.data ? reference.data.length : 0;
    const strip = longRecords && countLeadsWithData(ecgData) === 1;
    const pageSamples = windowSamplesFor(sampleRate, strip);
    const maxStartSamples = Math.max(0, totalSamples - pageSamples);

    return {
        totalSec: totalSamples / sampleRate,
        pageSec: pageSamples / sampleRate,
        rowSec: pageSamplesFor(sampleRate) / sampleRate,
        maxStartSec: maxStartSamples / sampleRate,
        paged: maxStartSamples > 0,
        strip: strip,
    }
}

/**
 * @param startSec
 * @param info
 */
export function clampWindowStart(startSec: number, info: EcgWindowInfo): number {
    if (typeof startSec !== "number" || isNaN(startSec)) {
        return 0
    }

    return Math.min(info.maxStartSec, Math.max(0, startSec))
}

/**
 * @param startSec
 * @param direction
 * @param info
 */
export function stepWindowStart(startSec: number, direction: -1 | 1, info: EcgWindowInfo): number {
    if (info.pageSec <= 0) {
        return 0
    }

    const page = Math.round(clampWindowStart(startSec, info) / info.pageSec);

    return clampWindowStart((page + direction) * info.pageSec, info)
}

/**
 *
 * @param seconds
 */
export function formatClock(seconds: number): string {
    const safe = typeof seconds === "number" && !isNaN(seconds) ? Math.max(0, seconds) : 0;
    const minutes = Math.floor(safe / 60);
    const rest = safe - (minutes * 60);
    const text = rest.toFixed(1);

    return `${minutes}:${rest < 10 ? `0${text}` : text}`
}

/**
 *
 * @param seconds
 */
export function formatDuration(seconds: number): string {
    if (typeof seconds !== "number" || isNaN(seconds) || seconds <= 0) {
        return "—"
    }

    return seconds < 60 ? `${seconds.toFixed(1)}s` : formatClock(seconds)
}
